// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import { SectionHeader, EditableInputComponent } from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import { useSession } from "@clerk/clerk-react";

// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { FileLabel } from "shared/types/file";
import { BuildingOwnerShipInfo } from "shared/types/ownership-info";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface BuildingTaxationInfoComponentProps {
  ownershipInfo: BuildingOwnerShipInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingTaxationInfoComponent = ({
  ownershipInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  building,
  unit,
  parentEditMode = true,
}: BuildingTaxationInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedOwnershipInfo, setEditedOwnershipInfo] = useState(ownershipInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setEditedOwnershipInfo(ownershipInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleOpenAddStabilityDocumentModal = () => {
    setFileModalOpen(true);
  };

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/building/building-ownership-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedOwnershipInfo),
        },
        session,
      );
      toast.success("Taxatie informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de taxatie informatie.",
      );
    }
  };

  const updateOwnershipInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedOwnershipInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...ownershipInfo,
        ...updatedFields,
      });
    }
  };

  const handleFileModalClose = () => {
    setFileModalOpen(false);
  };

  const handleFileModalSave = async (fileLogData: any) => {
    const newAppraisalFileLogs =
      fileLogData.customData?.savedBuildingOwnershipInfo.appraisalFileLogs;
    if (newAppraisalFileLogs) {
      setEditedOwnershipInfo((prev) => ({
        ...prev,
        appraisalFileLogs: newAppraisalFileLogs,
      }));
    }
    handleFileModalClose();
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Eigendomsinformatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Example: Appraisal Value */}
          <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-4">
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Schatting van de waarde</p>
            </header>
            <EditableInputComponent
              label="Geschatte vastgoedwaarde (excl. kosten) in euro"
              value={(editedOwnershipInfo.appraisalValue as any) || ""}
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateOwnershipInfo({ appraisalValue: Number(value) })
              }
            />
          </div>

          {/* Any additional components to fully flesh out the building's ownership details */}
        </div>

        <div className="col-span-full mt-8 flex flex-col gap-3">
          <FileLogTable
            acceptedLabels={[FileLabel.SchattingTaxatieverslag, FileLabel.TaxatieBerekeningen]}
            onUploadClick={handleOpenAddStabilityDocumentModal}
            building={building}
          />
        </div>
        {/* Modal for File Upload */}
        <ModalUploadFileLog
          isOpen={fileModalOpen}
          onClose={() => setFileModalOpen(false)}
          onSave={(fileLogData) => handleFileModalSave(fileLogData)}
          building={building}
          unit={unit}
          customLabels={[
            FileLabel.SchattingTaxatieverslag,
            FileLabel.TaxatieBerekeningen,
          ]}
        />
      </div>
    </section>
  );
};

export default BuildingTaxationInfoComponent;
